import React, { useState, useRef } from "react";
import { X, Upload, FileText, Trash2, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import { AppointmentService } from "../../../../services/api/appointment.service";
import { DetailedAppointment, DoctorDetail, PatientDetail } from "../types";

interface CompleteAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: DetailedAppointment;
  doctor?: DoctorDetail;
  patient: PatientDetail;
  reload: () => void;
}

export const CompleteAppointmentModal: React.FC<CompleteAppointmentModalProps> = ({
  isOpen,
  onClose,
  appointment,
  doctor,
  patient,
  reload,
}) => {
  const [chiefComplaints, setChiefComplaints] = useState("");
  const [prescriptionFile, setPrescriptionFile] = useState<File | null>(null);
  const [isUploadingPrescription, setIsUploadingPrescription] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a PDF or image file (PNG, JPG, JPEG)");
        return;
      }
      setPrescriptionFile(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a PDF or image file (PNG, JPG, JPEG)");
        return;
      }
      setPrescriptionFile(file);
    }
  };

  const handleRemoveFile = () => {
    setPrescriptionFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCompleteProcess = async (skipPrescription = false) => {
    if (!chiefComplaints.trim()) {
      toast.error("Please enter chief complaints");
      return;
    }

    const payload = {
      appointmentId: appointment.id,
      patientId: patient.id,
      patientName: patient.name,
      patientAge: patient.age,
      patientGender: patient.gender,
      doctorId: appointment.doctorId,
      doctorName: doctor?.name,
      diagnosis: "",
      chiefComplaints,
      specialInstruction: "",
      followUpDate: "",
      tests: [],
      medications: [],
      sendPrescription: false, // Always false as specified
    };

    // If a prescription file is uploaded and not skipped, upload it first
    if (prescriptionFile && !skipPrescription) {
      setIsUploadingPrescription(true);
      try {
        const uploadRes = await AppointmentService.uploadPrescription(
          appointment.id,
          prescriptionFile
        );

        if (!uploadRes.success) {
          toast.error(uploadRes.message || "Failed to upload prescription document");
          setIsUploadingPrescription(false);
          return;
        }
        toast.success("Prescription uploaded successfully");
      } catch (error) {
        toast.error("Failed to upload prescription document");
        setIsUploadingPrescription(false);
        return;
      } finally {
        setIsUploadingPrescription(false);
      }
    }

    // Mark appointment completed
    setIsCompleting(true);
    try {
      const response = await AppointmentService.markAppointmentCompleted(payload);
      if (response.success) {
        toast.success("Appointment marked as completed successfully");
        onClose();
        reload();
      } else {
        toast.error(response.message || "Failed to complete appointment");
      }
    } catch (error) {
      toast.error("Failed to complete appointment");
    } finally {
      setIsCompleting(false);
    }
  };

  const isBusy = isUploadingPrescription || isCompleting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden border border-gray-100 my-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Complete Appointment</h3>
              <p className="text-xs text-gray-500">Record chief complaints and attach prescription</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:bg-gray-100 hover:text-gray-600 p-1.5 rounded-lg transition-colors cursor-pointer"
            disabled={isBusy}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          {/* Chief Complaints Input */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-xs font-bold text-gray-700">
                Chief Complaints <span className="text-rose-500">*</span>
              </label>
              <span className="text-[10px] text-gray-400 font-medium">Required</span>
            </div>
            <textarea
              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-xs resize-none transition-all placeholder:text-gray-400"
              rows={3}
              placeholder="Enter patient's primary symptoms, diagnosis notes, or chief complaints..."
              value={chiefComplaints}
              onChange={(e) => setChiefComplaints(e.target.value)}
              disabled={isBusy}
            />
          </div>

          {/* Upload Prescription Section */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-xs font-bold text-gray-700">
                Upload Prescription Document
              </label>
              <span className="text-[10px] text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
                Optional
              </span>
            </div>

            {!prescriptionFile ? (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all ${
                  dragActive
                    ? "border-emerald-500 bg-emerald-50/50"
                    : "border-gray-200 hover:border-emerald-400 hover:bg-gray-50/60"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,image/png,image/jpeg,image/jpg"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isBusy}
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-full">
                    <Upload className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">
                      Click to browse or drag & drop prescription
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Supports PDF, PNG, JPG (up to 10MB)
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3.5 bg-emerald-50/60 border border-emerald-200 rounded-xl">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-white text-emerald-600 rounded-lg shadow-2xs shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-gray-900 truncate">
                      {prescriptionFile.name}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {(prescriptionFile.size / 1024 / 1024).toFixed(2)} MB •{" "}
                      {prescriptionFile.type.includes("pdf") ? "PDF Document" : "Image"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  disabled={isBusy}
                  className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer shrink-0 ml-2"
                  title="Remove file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer / Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-100 gap-2.5 bg-gray-50/50">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            disabled={isBusy}
          >
            Cancel
          </button>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {/* Skip Prescription Option button if file is attached or if user wants to explicitly skip */}
            {prescriptionFile ? (
              <button
                type="button"
                onClick={() => handleCompleteProcess(true)}
                disabled={isBusy || !chiefComplaints.trim()}
                className="px-3.5 py-2 border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
              >
                Skip Prescription & Complete
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleCompleteProcess(true)}
                disabled={isBusy || !chiefComplaints.trim()}
                className="px-3.5 py-2 border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
              >
                Skip Upload & Complete
              </button>
            )}

            {/* Primary Action Button */}
            <button
              onClick={() => handleCompleteProcess(false)}
              className="px-5 py-2 bg-emerald-600 rounded-xl text-xs font-bold text-white hover:bg-emerald-700 transition-all shadow-sm active:scale-95 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
              disabled={isBusy || !chiefComplaints.trim()}
            >
              {isBusy ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>
                    {isUploadingPrescription
                      ? "Uploading Prescription..."
                      : "Completing Appointment..."}
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{prescriptionFile ? "Upload & Complete" : "Complete Appointment"}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
